import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, ButtonBase, Stack, Tooltip, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useSmartTranslation } from '../../../../../../../generic/hooks/useSmartTranslation'
import { FeatureIndicatorSlider } from '../../../../../Inspection/FeatureIndicatorSlider'
import { EMPTY_VALUE, boundedRatio, formatQuantity } from '../../../utils/jobDetailFormat'
import {
  INSPECTION_RESULT,
  deviationBarShown,
  groupInspections,
  isReportable,
  resultKind,
  signedQuantity,
  specificationText,
  typeLabelKey
} from '../../../utils/jobDetailInspections'
import { BlockState, DetailSection, EllipsisText, HeaderCell, Pill, ProgressBar } from '../JobDetailPrimitives'

// Historia 160, enmienda E-11 (CU6, RN58-RN67, 03-dominio §"Enmienda E-11" §9) — INSPECCIONES DEL JOB por
// tipo de característica, como el modal "Open" del widget Inspection: una sección por grupo (abierta al
// entrar) con el chip "reportadas / reportables" y 7 columnas por característica. Todo lo que depende del
// tipo sale de `utils/jobDetailInspections`; un contador null se pinta "—", nunca 0.

const GRID = 'minmax(0, 0.9fr) minmax(0, 2fr) minmax(0, 0.8fr) minmax(0, 1.3fr) minmax(0, 1.5fr) minmax(0, 1.2fr) minmax(0, 1fr)'

// A 1920×1080 la cabecera, los KPIs, la secuencia y el título de la pestaña ocupan ~590 px: el resto es
// de la tabla, que desplaza DENTRO de la pestaña (RN47, S134).
const SCROLL_MAX_HEIGHT = 'calc(100vh - 590px)'

const countText = (value) => (value === null || value === undefined ? EMPTY_VALUE : String(value))

const ResultCell = ({ line, language }) => {
  const { t_ } = useSmartTranslation()
  const code = line.code
  switch (resultKind(line)) {
    case INSPECTION_RESULT.RANGE: {
      const out = line.outOfTolerance || 0
      return (
        <Stack role='cell' spacing={0.5} data-testid={'inspection-result-' + code} sx={{ minWidth: 0 }}>
          <Typography data-testid={'inspection-verdict-' + code} sx={{ fontWeight: 700 }}>
            <Box component='span' data-testid={'inspection-correct-' + code} data-tone='success' sx={{ color: 'primary.main' }}>{countText(line.correct)} ✓</Box>
            {' / '}
            <Box component='span' data-testid={'inspection-out-' + code} data-tone={out > 0 ? 'warning' : 'neutral'} sx={{ color: out > 0 ? 'yellow.main' : 'text.secondary' }}>{countText(line.outOfTolerance)} ✗</Box>
          </Typography>
          {deviationBarShown(line) ? <DeviationBar line={line} language={language} /> : null}
        </Stack>
      )
    }
    case INSPECTION_RESULT.BOOLEAN:
      return (
        <Typography role='cell' data-testid={'inspection-result-' + code} sx={{ fontWeight: 700 }}>
          {t_('OK').trim() + ' ' + countText(line.correct) + ' / ' + t_('NOK').trim() + ' ' + countText(line.outOfTolerance)}
        </Typography>
      )
    case INSPECTION_RESULT.NO_VERDICT:
      return (
        <Stack role='cell' direction='row' alignItems='center' spacing={1} data-testid={'inspection-result-' + code} sx={{ minWidth: 0 }}>
          <Typography>{t_('{{count}} reported', { count: countText(line.reportedParts) })}</Typography>
          <Pill label={t_('No verdict')} testId={'inspection-no-verdict-' + code} />
        </Stack>
      )
    case INSPECTION_RESULT.REPORTED:
      return <Typography role='cell' data-testid={'inspection-result-' + code}>{t_('{{count}} reported', { count: countText(line.reportedParts) })}</Typography>
    default:
      return <Typography role='cell' data-testid={'inspection-result-' + code} color='text.secondary'>{EMPTY_VALUE}</Typography>
  }
}

ResultCell.propTypes = { line: PropTypes.object, language: PropTypes.string }

// RN61/D32: la mini barra del modal (`FeatureIndicatorSlider`, reutilizado tal cual) con el ÚLTIMO valor, en
// solo lectura; el tooltip dice el valor, la unidad y la desviación con su signo ("E-11 precisiones" (2)).
// UX-9 (historia 169): EXPORTADA para que la fila INSPECCIÓN de la huella la reutilice tal cual (con un
// adaptador de nombres de campo, `lastValue`/`lastDeviation`) en vez de duplicarla.
export const DeviationBar = ({ line, language }) => {
  const { t_ } = useSmartTranslation()
  const value = t_('Last value {{value}}', { value: formatQuantity(line.lastValue, language) }) + (line.unit ? ' ' + line.unit : '')
  const deviation = line.lastDeviation === null || line.lastDeviation === undefined
    ? ''
    : ' · ' + t_('Deviation') + ' ' + signedQuantity(line.lastDeviation, language)
  return (
    <Tooltip title={value + deviation}>
      <Box data-testid={'inspection-deviation-' + line.code} data-value={String(line.lastValue)} sx={{ width: 'fit-content' }}>
        <FeatureIndicatorSlider
          value={line.lastValue}
          nominalValue={line.nominal}
          upperTolerance={line.upperTolerance}
          lowerTolerance={line.lowerTolerance}
          sx={{ width: '7em', py: 0.5, pointerEvents: 'none' }}
        />
      </Box>
    </Tooltip>
  )
}

DeviationBar.propTypes = { line: PropTypes.object, language: PropTypes.string }

const InspectionRow = ({ line, expected, language }) => {
  const { t_ } = useSmartTranslation()
  const code = line.code
  const reportable = isReportable(line)
  const reported = line.reportedParts || 0
  const typeChip = <Pill label={t_(typeLabelKey(line.dataType))} testId={'inspection-type-' + code} />
  const toolNames = (line.tools || []).map((tool) => tool.name).filter((name) => name)
  return (
    <Box
      role='row'
      data-testid={'inspection-row-' + code}
      sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2, py: 1, minHeight: 54, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}
    >
      <Typography role='cell' data-testid={'inspection-code-' + code} sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{code}</Typography>
      <Box role='cell' sx={{ minWidth: 0 }}>
        <Stack direction='row' alignItems='center' spacing={1} sx={{ minWidth: 0 }}>
          <Typography noWrap title={line.name} data-testid={'inspection-name-' + code} sx={{ minWidth: 0 }}>{line.name}</Typography>
          {(line.frequency || 0) > 1
            ? <Pill label={t_('1 of every {{count}}', { count: line.frequency })} tone='info' testId={'inspection-frequency-' + code} />
            : null}
        </Stack>
        {(line.question || '').trim() !== ''
          ? <Typography variant='body2' color='text.secondary' data-testid={'inspection-question-' + code}>{line.question}</Typography>
          : null}
      </Box>
      <Box role='cell'>
        {line.typeName ? <Tooltip title={line.typeName}><span>{typeChip}</span></Tooltip> : typeChip}
      </Box>
      <Typography role='cell' data-testid={'inspection-spec-' + code} sx={{ overflowWrap: 'anywhere' }}>{specificationText(line, t_, language)}</Typography>
      <ResultCell line={line} language={language} />
      <Stack role='cell' direction='row' alignItems='center' spacing={1.5} data-testid={'inspection-reported-cell-' + code}>
        {reportable ? <ProgressBar value={boundedRatio(reported, expected)} sx={{ flexGrow: 1 }} label={code} /> : null}
        <Typography sx={{ minWidth: 72, textAlign: 'right' }} data-testid={'inspection-reported-' + code}>
          {!reportable || line.reportedParts === null || line.reportedParts === undefined
            ? EMPTY_VALUE
            : reported > 0 ? reported + ' / ' + expected : t_('Not reported')}
        </Typography>
      </Stack>
      <Box role='cell' sx={{ minWidth: 0 }}>
        <EllipsisText text={toolNames.length > 0 ? toolNames.join(', ') : EMPTY_VALUE} testId={'inspection-tools-' + code} />
      </Box>
    </Box>
  )
}

InspectionRow.propTypes = { line: PropTypes.object, expected: PropTypes.number, language: PropTypes.string }

const InspectionGroup = ({ section, expected, language }) => {
  const { t_ } = useSmartTranslation()
  // Abierta al entrar; plegarla no se recuerda (§9).
  const [open, setOpen] = useState(true)
  const title = section.noGroup ? t_('No group') : section.name
  return (
    <Box data-testid='inspection-section' component='section' aria-label={title} sx={{ borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <ButtonBase
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        sx={{ width: '100%', justifyContent: 'flex-start', gap: 1.5, px: 2, py: 1.25, bgcolor: 'background.row2' }}
      >
        <ExpandMoreIcon fontSize='small' sx={{ color: 'text.secondary', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 120ms' }} />
        <Typography data-testid='inspection-section-title' sx={{ fontWeight: 800 }}>{title}</Typography>
        <Pill label={section.reported + ' / ' + section.reportable} tone={section.reportable > 0 && section.reported === section.reportable ? 'success' : 'default'} testId='inspection-section-chip' />
      </ButtonBase>
      {open
        ? (
          <Box role='table' aria-label={title}>
            <Box role='row' sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <HeaderCell>{t_('Code')}</HeaderCell>
              <HeaderCell>{t_('Inspection')}</HeaderCell>
              <HeaderCell>{t_('Type')}</HeaderCell>
              <HeaderCell>{t_('Specification')}</HeaderCell>
              <HeaderCell>{t_('Result')}</HeaderCell>
              <HeaderCell>{t_('Reported parts')}</HeaderCell>
              <HeaderCell>{t_('Inspection Tool')}</HeaderCell>
            </Box>
            {section.lines.map((line) => <InspectionRow key={line.featureId} line={line} expected={expected} language={language} />)}
          </Box>
          )
        : null}
    </Box>
  )
}

InspectionGroup.propTypes = { section: PropTypes.object, expected: PropTypes.number, language: PropTypes.string }

const InspectionsTab = ({ block = {}, onRetry = () => {} }) => {
  const { t_, i18n } = useSmartTranslation()
  const language = i18n?.language || 'en'
  const expected = block.data?.expectedParts || 0
  const sections = groupInspections(block.data?.inspections || [])
  return (
    <DetailSection title={t_('Job inspections')} testId='inspections-block'>
      <BlockState status={block.status} onRetry={onRetry}>
        <Box data-testid='inspections-scroll' sx={{ maxHeight: SCROLL_MAX_HEIGHT, overflowY: 'auto' }}>
          {sections.map((section) => <InspectionGroup key={section.key} section={section} expected={expected} language={language} />)}
        </Box>
      </BlockState>
    </DetailSection>
  )
}

InspectionsTab.propTypes = { block: PropTypes.object, onRetry: PropTypes.func }

export default React.memo(InspectionsTab)
